import type {
  CartStoreItem,
  CartTuitionItem,
  EnrolledProgram,
  StoreProduct,
} from '@/features/payments/types';
import { formatPaymentMoney } from '@/features/payments/currency';

export function formatKd(amount: number): string {
  return formatPaymentMoney(amount);
}

export function formatKwd(amount: number): string {
  return formatPaymentMoney(amount);
}

export function getProductPriceForChild(product: StoreProduct, childId: string): number {
  return product.pricesByChild?.[childId]?.amount ?? product.price;
}

export function repricePaymentCart(
  tuitionItems: readonly CartTuitionItem[],
  storeItems: readonly CartStoreItem[],
  programs: readonly EnrolledProgram[] | undefined,
  products: readonly StoreProduct[] | undefined,
): { tuitionItems: CartTuitionItem[]; storeItems: CartStoreItem[] } {
  return {
    tuitionItems: tuitionItems.flatMap((item) => {
      // Preserve the cart until the overview is available; only reconcile confirmed API data.
      if (!programs) return [item];
      const program = programs.find(
        (candidate) =>
          candidate.billingProgramId === item.billingProgramId &&
          candidate.childId === item.childId,
      );
      const fee = program?.fees.find((candidate) => candidate.id === item.feeId);
      if (!program || !fee || fee.paid) return [];
      const amount =
        item.isCustomAmountAllocation && fee.amount != null
          ? Math.min(item.amount, fee.amount)
          : fee.amount;
      const originalAmount = fee.originalAmount;
      return amount != null
        ? [
            {
              ...item,
              programTitle: program.title,
              label: fee.label,
              amount,
              originalAmount:
                originalAmount != null && originalAmount > amount ? originalAmount : undefined,
              discountCode: fee.discountCode,
              discountType: fee.discountType,
              discountValue: fee.discountValue,
            },
          ]
        : [item];
    }),
    storeItems: storeItems.flatMap((item) => {
      if (!products) return [item];
      const product = products.find((candidate) => candidate.id === item.productId);
      return product
        ? [
            {
              ...item,
              name: product.name,
              price: getProductPriceForChild(product, item.childId),
              originalPrice: product.price,
              discountCode: product.pricesByChild?.[item.childId]?.discountCode,
              discountType: product.pricesByChild?.[item.childId]?.discountType,
              discountValue: product.pricesByChild?.[item.childId]?.discountValue,
            },
          ]
        : [];
    }),
  };
}

export function getInsufficientInventoryProduct(
  items: readonly CartStoreItem[],
  products: readonly StoreProduct[],
): StoreProduct | undefined {
  return products.find((product) => {
    if (!product.isPhysical || product.inventory == null) return false;
    const requested = items
      .filter((item) => item.productId === product.id)
      .reduce((sum, item) => sum + item.quantity, 0);
    return requested > product.inventory;
  });
}

export function getTuitionCartTotal(
  items: readonly CartTuitionItem[],
  excludedKeys: ReadonlySet<string> = new Set(),
): number {
  return items
    .filter((item) => !excludedKeys.has(item.id))
    .reduce((sum, item) => sum + item.amount, 0);
}

export function getStoreCartTotal(items: readonly CartStoreItem[]): number {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
}

export function getPaymentsCartTotal(
  tuitionItems: readonly CartTuitionItem[],
  storeItems: readonly CartStoreItem[],
  excludedTuitionKeys: readonly string[] = [],
): number {
  const excluded = new Set(excludedTuitionKeys);
  return getTuitionCartTotal(tuitionItems, excluded) + getStoreCartTotal(storeItems);
}

export function getPaymentsCartCount(
  tuitionItems: readonly CartTuitionItem[],
  storeItems: readonly CartStoreItem[],
): number {
  return tuitionItems.length + storeItems.reduce((sum, item) => sum + item.quantity, 0);
}

export function tuitionCartKey(programId: string, feeId: string): string {
  return `${programId}:${feeId}`;
}

function serializeStoreOptions(
  options?: Readonly<Record<string, string | readonly string[]>>,
): string {
  if (!options || Object.keys(options).length === 0) return '';
  const sortedKeys = Object.keys(options).sort();
  return JSON.stringify(sortedKeys.map((key) => [key, options[key]]));
}

export function storeCartKey(
  productId: string,
  childId: string,
  options?: Readonly<Record<string, string | readonly string[]>>,
): string {
  return `store:${productId}:${childId}:${serializeStoreOptions(options)}`;
}

export function storeCartGroupKey(
  productId: string,
  options?: Readonly<Record<string, string | readonly string[]>>,
): string {
  return storeCartKey(productId, '_', options);
}
